/**
 * Post-auth destination handling.
 *
 * A visitor who is bounced to the login page from somewhere (most often
 * /checkout, now that login is only required at payment) must land back where
 * they were going. Two carriers exist for that intent:
 *
 *   • ?returnUrl=…  — survives a full page load, so it is the one that works for
 *                     the Google sign-in round trip and for a pasted link.
 *   • router state `from` — the original mechanism, still honoured.
 *
 * The query param wins when both are present.
 */

import type { Location } from 'react-router-dom'

const DEFAULT_DESTINATION = '/'

/** `/login?returnUrl=<encoded path>` — omitting the param for the home page. */
export function buildLoginUrl(destination: string, loginPath = '/login'): string {
  if (!destination || destination === DEFAULT_DESTINATION) return loginPath
  return `${loginPath}?returnUrl=${encodeURIComponent(destination)}`
}

/**
 * Where to send the user after a successful login/signup.
 *
 * Only SAME-ORIGIN, absolute-path destinations are accepted. An attacker-supplied
 * `?returnUrl=https://evil.example` would otherwise turn our login page into an
 * open redirect, which is a real phishing primitive — anything that isn't a
 * plain "/path" falls back to the default.
 */
export function resolveReturnUrl(location: Location): string {
  const param = new URLSearchParams(location.search).get('returnUrl')
  if (param && isSafeInternalPath(param)) return param

  const from = (location.state as any)?.from?.pathname
  if (typeof from === 'string' && isSafeInternalPath(from)) return from

  return DEFAULT_DESTINATION
}

/**
 * True when the visitor was sent here *from somewhere* — a ?returnUrl or the
 * router state — rather than arriving at /login or /signup directly.
 *
 * `resolveReturnUrl` can't answer this: it returns "/" both for "no destination
 * was given" and for "the destination is the home page". The OTP flow needs the
 * difference, because a brand-new account with nowhere particular to be should
 * start the quiz, while one bounced out of /checkout must go back to checkout.
 */
export function hasReturnUrl(location: Location): boolean {
  const param = new URLSearchParams(location.search).get('returnUrl')
  if (param && isSafeInternalPath(param)) return true

  const from = (location.state as any)?.from?.pathname
  return typeof from === 'string' && isSafeInternalPath(from)
}

/**
 * True when this visitor was sent here by the craft flow — they finished all ten
 * questions and their formula is generated the moment they have an account.
 *
 * The auth forms use it to say so. Someone who has just answered ten questions
 * and is met with a bare "Welcome back" has no way of knowing their answers were
 * kept, and abandoning there costs the completed quiz.
 */
export function isFormulationReturn(location: Location): boolean {
  return resolveReturnUrl(location).startsWith('/formulation')
}

function isSafeInternalPath(value: string): boolean {
  // Must start with a single "/" — rejects "https://…", "//evil.example"
  // (protocol-relative) and "javascript:" alike.
  if (!value.startsWith('/') || value.startsWith('//')) return false
  // Never bounce back to the auth pages themselves; that's a loop.
  return !/^\/(login|signup)(\/|\?|$)/.test(value)
}

/**
 * Carry the current returnUrl across the login ⇄ signup link, so a visitor who
 * decides to create an account instead still lands on their destination.
 */
export function preserveReturnUrl(location: Location, targetPath: string): string {
  const param = new URLSearchParams(location.search).get('returnUrl')
  if (param && isSafeInternalPath(param)) {
    return `${targetPath}?returnUrl=${encodeURIComponent(param)}`
  }
  return targetPath
}
